/* eslint-disable react/function-component-definition */
import { EditOutlined, DeleteOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { VariableType } from '../../../../../components/Table/types'
import { userCompaniesSelector } from '../../../../../redux/session/currentUser/selectors'
import { useAccounts } from '../../../../../features/account/queries/useFetchAccounts'
import {
  ReallocationSource,
  ReallocationType,
  ReallocationTarget,
  ReallocationTargetType
} from '../../../../../types/reallocation/Reallocation'
import { formatValueByType } from '../../../../../utils/helpers'
import { ReallocationContext, ReallocationContextType } from './ReallocationProvider'
import { useMultipleCompanyDimensions } from '../../../../../features/dimension/queries/useMultipleCompanyDimensions'
import { useDeleteConfirmModal } from '../../../../../shared/modals/deleteConfirmModal'

interface ReallocationTargetTableProps {
  reallocation: ReallocationSource
  targets: ReallocationTarget[]
}
const ReallocationTargetTable = ({ reallocation, targets }: ReallocationTargetTableProps) => {
  const dimensions = useMultipleCompanyDimensions(targets?.map(t => t.companyId) || [])
  const companies = useSelector(userCompaniesSelector)
  const { t } = useTranslation()
  const { data: accounts } = useAccounts()
  const { handleNewTarget, handleEditTarget, handleCopyTarget, handleDeleteTarget } = useContext(
    ReallocationContext
  ) as ReallocationContextType
  const deleteConfirmModal = useDeleteConfirmModal()

  const percentFormat = (val: string | number) => {
    return `${formatValueByType(+val, VariableType.percentage, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })}`
  }

  const getDimensionName = (dimensionId: string) => {
    return dimensions?.flatMap(dimension => dimension.data || []).find(item => item.dimensionId === dimensionId)?.name
  }

  const targetLabel = (record: ReallocationTarget) => {
    if (record.type === ReallocationTargetType.range) {
      return `${record.accountFrom} - ${record.accountTo}`
    }
    const selectedAccount = accounts?.find(account => account.code === record.account)
    return selectedAccount && `${selectedAccount.code} - ${selectedAccount.name}`
  }

  const targetDeleteLabel = (record: ReallocationTarget) => {
    const companyName = companies?.find(company => company.id === record.companyId)?.name
    const dimensionName = record.dimensionId && getDimensionName(record.dimensionId)
    return [companyName, dimensionName].filter(Boolean).join(' - ')
  }

  const targetColumns: ColumnsType<ReallocationTarget> = [
    {
      key: 'companyId',
      title: t('global:company'),
      dataIndex: 'companyId',
      render: val => companies && companies?.find(d => d.id === val)?.name,
      sorter: (a, b) => {
        const aName = companies && companies?.find(d => d.id === a.companyId)?.name
        const bName = companies && companies?.find(d => d.id === b.companyId)?.name
        return aName?.localeCompare(bName || '') || 0
      }
    },
    {
      key: 'dimensionId',
      title: t('global:dimension'),
      dataIndex: 'dimensionId',
      render: (dimensionId: string) => getDimensionName(dimensionId),
      sorter: (a, b) => {
        const aName = a.dimensionId && getDimensionName(a.dimensionId)
        const bName = b.dimensionId && getDimensionName(b.dimensionId)
        return aName?.localeCompare(bName || '') || 0
      }
    },
    {
      key: 'accounts',
      title: t('reallocationsPage:accounts'),
      render: (val, record) => `${record.accountFrom} - ${record.accountTo}`
    },
    {
      key: 'type',
      title: t('reallocationsPage:targetAccounts'),
      render: (val, record) => targetLabel(record)
    }
  ]

  if (reallocation.type === ReallocationType.fixed) {
    targetColumns.push({
      key: 'fixed',
      title: t('reallocationsPage:fixed'),
      dataIndex: 'fixed',
      align: 'right',
      render: percentFormat,
      sorter: (a, b) => a.fixed - b.fixed
    })
  }

  targetColumns.push({
    key: 'actions',
    align: 'right',
    title: t('global:actions'),
    dataIndex: 'dimensionId',
    render: (val: string, record) => (
      <Space>
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => {
            handleEditTarget(record, reallocation)
          }}
        />
        <Button
          type="text"
          icon={<CopyOutlined />}
          onClick={() => {
            handleCopyTarget(record, reallocation)
          }}
        />
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            deleteConfirmModal({
              content: targetDeleteLabel(record),
              onOk: () => handleDeleteTarget(record, reallocation)
            })
          }
        />
      </Space>
    )
  })

  const fixedSumRowRender = (pageData: readonly ReallocationTarget[]) => {
    let totalFixed = 0

    pageData.forEach(({ fixed }) => {
      totalFixed += +fixed
    })

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>{t('loans:total-value')}</Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={3} />
        <Table.Summary.Cell align="right" index={4}>
          {percentFormat(totalFixed)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} />
      </Table.Summary.Row>
    )
  }

  return (
    <div>
      <Table
        rowKey="id"
        pagination={false}
        dataSource={targets?.sort((a, b) => a.id - b.id)}
        columns={targetColumns}
        summary={reallocation.type === ReallocationType.fixed ? fixedSumRowRender : undefined}
      />
      <Button
        icon={<PlusOutlined />}
        style={{ marginTop: '4px' }}
        block
        color="primary"
        variant="dashed"
        onClick={() => handleNewTarget(reallocation)}
      >
        {t('global:add-new')}
      </Button>
    </div>
  )
}

export default ReallocationTargetTable
